import axios from "axios";
import { create } from "zustand";

interface SubeventSelection {
    subeventId: number,
    role: number,
    partnerId: number | null
}

interface GuestInfo {
    name: string,
    surName: string,
    email: string,
    address: string,
    wsdcId: string | null
}

interface Partner {
    id: number,
    name: string,
    surName: string,
    email: string,
    discount: number,
    wsdcId: string | null,
    discountCode: string | null
}

interface PassSelected {
    passId: number | null,
    role: number,
    partner: Partner | null
}

interface PrebookingRequest {
    eventId: number,
    pass: PassSelected | null,
    discount: number | null,
    discountCode: string | null,
    partners: Partner[],
    guestInfo: GuestInfo | null,
    subevents: SubeventSelection[],
    infoProvided: boolean,
}

interface PrebookingRequestStore {
    prebooking: PrebookingRequest | null,
    createNewPrebooking: (eventId: number) => void,
    addSubevent: (subeventId: number, role: number, partnerId: number | null, callback: Function) => void,
    removeSubevent: (subeventId: number, callback: Function) => void,
    updateGuestInfo: (guestInfo: GuestInfo, callback: Function) => void,
    updatePass: (pass: PassSelected, callback: Function) => void,
    updateDiscount: (discount: number, callback: Function) => void,
    updateDiscountCode: (discountCode: string | null, callback: Function) => void,
    checkDiscountCode: (discountCode: string) => Promise<boolean>,
    addPartner: (name: string, surName: string, email: string, discount: number, wsdcId: string | null, discountCode: string | null, callback: Function) => void,
    removePartner: (id: number, callback: Function) => void,
    setInfoProvided: (infoProvided: boolean) => void,
    setWSDCId: (wsdcId: string | null) => void,
    getTrimmed: (prebookingRequest: PrebookingRequest) => PrebookingRequest
}

export const usePrebookingRequest = create<PrebookingRequestStore>((set, get) => ({
    prebooking: null,
    createNewPrebooking: (eventId: number) => {
        set({
            prebooking: {
                eventId: eventId,
                pass: null,
                discount: null,
                discountCode: null,
                partners: [],
                guestInfo: {
                    name: "",
                    surName: "",
                    email: "",
                    address: "",
                    wsdcId: null
                },
                subevents: [],
                infoProvided: false
            }
        });
    },
    addSubevent: (subeventId: number, role: number, partnerId: number | null, callback: Function) => {
        let prebooking = get().prebooking;
        if (prebooking) {
            prebooking.subevents.push({
                subeventId: subeventId,
                role: role,
                partnerId: partnerId
            });
            set({ prebooking: prebooking });
            callback(prebooking);
        }
    },
    removeSubevent: (subeventId: number, callback: Function) => {
        let prebooking = get().prebooking;
        if (prebooking) {
            prebooking.subevents = prebooking.subevents.filter(subevent => {
                return subevent.subeventId !== subeventId;
            });
            set({ prebooking: prebooking });
            callback(prebooking);
        }
    },
    updateGuestInfo: (guestInfo: GuestInfo, callback: Function) => {
        let prebooking = get().prebooking;
        if (prebooking) {
            prebooking.guestInfo = guestInfo;
            set({ prebooking: prebooking });
            callback(prebooking);
        }
    },
    updatePass: (pass: PassSelected, callback: Function) => {
        let prebooking = get().prebooking;
        if (prebooking) {
            prebooking.pass = pass;
            set({ prebooking: prebooking });
            callback(prebooking);
        }
    },
    updateDiscount: (discount: number, callback: Function) => {
        let prebooking = get().prebooking;
        if (prebooking) {
            prebooking.discount = discount;
            set({ prebooking: prebooking });
            callback(prebooking);
        }
    },
    updateDiscountCode: (discountCode: string | null, callback: Function) => {
        let prebooking = get().prebooking;
        if (prebooking) {
            prebooking.discountCode = discountCode;
            if (discountCode === "") prebooking.discount = null;
            set({ prebooking: prebooking });
            callback(prebooking);
        }
    },
    checkDiscountCode: async (discountCode: string) => {
        try {
            await axios.get(`wp-json/tts-booking/v1/events/discountcode?code=${discountCode}`);
            return true;
        } catch (err) {
            return false;
        }
    },
    addPartner: (name: string, surName: string, email: string, discount: number, wsdcId: string | null, discountCode: string | null, callback: Function) => {
        let prebooking = get().prebooking;
        if (prebooking) {
            let id = prebooking.partners.length;
            prebooking.partners.push({
                id: id,
                name: name,
                surName: surName,
                email: email,
                discount: discount,
                wsdcId: wsdcId,
                discountCode: discountCode,
            });
            set({ prebooking: prebooking });
            callback(id);
        }
    },
    removePartner: (id: number, callback: Function) => {
        let prebooking = get().prebooking;
        if (prebooking) {
            prebooking.partners = prebooking.partners.filter(partner => {
                return partner.id !== id;
            });
            set({ prebooking: prebooking });
            callback(prebooking);
        }
    },
    setInfoProvided: (infoProvided: boolean) => {
        let prebooking = get().prebooking;
        if (prebooking) {
            prebooking.infoProvided = infoProvided;
            set({ prebooking: prebooking });
        }
    },
    setWSDCId: (wsdcId: string | null) => {
        let prebooking = get().prebooking;
        if (prebooking && prebooking.guestInfo) {
            prebooking.guestInfo.wsdcId = wsdcId;
            set({ prebooking: prebooking });
        }
    },
    getTrimmed: (preBookingRequest: PrebookingRequest) => {
        let newPreBookingRequest = {
            eventId: preBookingRequest.eventId,
            pass: preBookingRequest.pass,
            discount: preBookingRequest.discount,
            discountCode: preBookingRequest.discountCode?.trim() ?? null,
            partners: preBookingRequest.partners.map(partner => {
                return {
                    id: partner.id,
                    name: partner.name.trim(),
                    surName: partner.surName.trim(),
                    email: partner.email.trim(),
                    discount: partner.discount,
                    wsdcId: partner.wsdcId?.trim() ?? null,
                    discountCode: partner.discountCode?.trim() ?? null
                }
            }),
            guestInfo: {
                name: preBookingRequest.guestInfo?.name.trim() ?? "",
                surName: preBookingRequest.guestInfo?.surName.trim() ?? "",
                email: preBookingRequest.guestInfo?.email.trim() ?? "",
                address: preBookingRequest.guestInfo?.address.trim() ?? "",
                wsdcId: preBookingRequest.guestInfo?.wsdcId?.trim() ?? null
            },
            subevents: preBookingRequest.subevents,
            infoProvided: preBookingRequest.infoProvided
        }
        return newPreBookingRequest;
    }
}));

export type { PrebookingRequest, GuestInfo, SubeventSelection, PassSelected };